import { arSceneIdOf } from "./arKeys";

/**
 * 🪄 **이 게시로 마법이 사라지나** — 올리기 직전에 묻기 위한 판정.
 *
 * ## 🔴 왜 (2026-09-10 운영 사고)
 * 7쪽의 마법 정의가 **아무 예고 없이 게시본에서 사라졌다.** 카메라 타깃을 다시 외우게 하는
 * 단추가 그 자리를 `{ url, alt }` 로 **통째로 갈아 끼우고** 있었고(그 자리에 `interact` 가 같이
 * 산다), 관리자는 그 뒤로 「층 0」만 보여 줬다 — 만든 적 없는 것처럼. 게시도 조용히 나갔다.
 *
 * 그 한 줄은 고쳤다. 하지만 **자리를 통째로 쓰는 코드는 앞으로도 또 생긴다**(같은 자리에 쓰는
 * 곳이 이미 넷이다). 그래서 마지막 문 앞에 한 번 더 센다: **올리면 무엇이 사라지는가.**
 * 사람이 지우려던 것이면 그대로 진행하고, 아니면 멈춘다.
 *
 * 판정만 여기 둔다 — 묻는 일은 화면이 한다.
 */
export interface MagicLoss {
  /** `<책>.<쪽>` 또는 카드 장면 id. */
  sceneId: string;
  /** 사라지는 층 수(무엇을 잃는지 크기를 보여 준다). */
  layers: number;
  /** 자리 자체가 없어지나(`true`) 아니면 마법만 빠지나(`false`). */
  gone: boolean;
}

/**
 * 🔴 **여기서 보는 것만 적는다.** 자리 항목 전체 모양(`AssetManifestEntry`)을 요구하면
 * 부르는 쪽이 매번 형을 맞춰 넣어야 한다 — 이 판정은 `interact` 하나만 본다.
 */
type Entry = { interact?: unknown } | undefined;
const layersOf = (e: Entry): number => {
  const l = (e?.interact as { layers?: unknown[] } | undefined)?.layers;
  return Array.isArray(l) ? l.length : 0;
};

/**
 * 지금 게시본(`before`)에는 마법이 있는데 올리려는 것(`after`)에는 없는 자리들.
 *
 * 🔴 **「없어짐」만 센다.** 바뀌는 것은 세지 않는다 — 자리를 옮기거나 층을 지우는 것은
 * 운영자가 하려던 일이고, 매번 물으면 곧 아무도 안 읽는다.
 */
export function magicLossOnPublish(
  before: Record<string, Entry>,
  after: Record<string, Entry>,
): MagicLoss[] {
  const out: MagicLoss[] = [];
  for (const [key, was] of Object.entries(before)) {
    if (!key.startsWith("ar.target.")) continue;
    const n = layersOf(was);
    if (n === 0) continue; // 원래 마법이 없던 자리
    const now = after[key];
    if (layersOf(now) > 0) continue; // 그대로 있다
    const sceneId = arSceneIdOf(key);
    if (sceneId) out.push({ sceneId, layers: n, gone: !now });
  }
  return out;
}

/** 사람에게 보여 줄 한 줄 — 화면·검사가 같은 말을 쓰게. */
export function magicLossSay(loss: readonly MagicLoss[], where: (sceneId: string) => string = (s) => s): string {
  if (loss.length === 0) return "";
  const items = loss.map((l) => `· ${where(l.sceneId)} — 층 ${l.layers}개`).join("\n");
  return `🪄 이 게시로 **마법 ${loss.length}곳이 사라져요.** 아이가 눌러도 아무 일이 안 나게 돼요.\n${items}`;
}
